import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { userApi, merchantApi, auth } from '../../services/api';
import { AdminHeader, PrimaryBtn, GhostBtn, EmptyState } from './AdminLayout';

const CAP_OPTIONS = [
  { key: 'article', label: '文章运营' },
  { key: 'tandian', label: '探店运营' },
];
const capLabel = (k) => CAP_OPTIONS.find((c) => c.key === k)?.label || k;

export default function UserManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);

  // 新建
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', assignedMerchants: [], capabilities: ['article'] });
  const [saving, setSaving] = useState(false);

  // 分配商家
  const [assigning, setAssigning] = useState(null); // { id, username, set:Set }
  const [savingAssign, setSavingAssign] = useState(false);

  // 功能权限
  const [editingCaps, setEditingCaps] = useState(null); // { id, username, set:Set }
  const [savingCaps, setSavingCaps] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([userApi.list(), merchantApi.list()])
      .then(([u, m]) => { setUsers(u); setMerchants(m); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!auth.isSuperadmin()) { navigate('/admin', { replace: true }); return; }
    load();
  }, [load, navigate]);

  const merchantName = (id) => merchants.find(m => m._id === id)?.name || '已删除商家';

  const toggleFormMerchant = (id) => {
    setForm(f => ({
      ...f,
      assignedMerchants: f.assignedMerchants.includes(id)
        ? f.assignedMerchants.filter(x => x !== id)
        : [...f.assignedMerchants, id],
    }));
  };

  const toggleFormCap = (key) => {
    setForm(f => ({
      ...f,
      capabilities: f.capabilities.includes(key)
        ? f.capabilities.filter(x => x !== key)
        : [...f.capabilities, key],
    }));
  };

  const handleCreate = async () => {
    if (!form.username.trim() || !form.password.trim()) return;
    setSaving(true);
    try {
      await userApi.create({
        username: form.username.trim(),
        password: form.password.trim(),
        assignedMerchants: form.assignedMerchants,
        capabilities: form.capabilities,
      });
      setForm({ username: '', password: '', assignedMerchants: [], capabilities: ['article'] });
      setCreating(false);
      load();
    } catch (e) {
      alert(e.response?.data?.error || '创建失败');
    } finally {
      setSaving(false);
    }
  };

  const openAssign = (u) => {
    setAssigning({ id: u._id, username: u.username, set: new Set(u.assignedMerchants || []) });
  };

  const toggleAssign = (id) => {
    setAssigning(a => {
      const set = new Set(a.set);
      set.has(id) ? set.delete(id) : set.add(id);
      return { ...a, set };
    });
  };

  const handleSaveAssign = async () => {
    setSavingAssign(true);
    try {
      await userApi.update(assigning.id, { assignedMerchants: Array.from(assigning.set) });
      setAssigning(null);
      load();
    } finally {
      setSavingAssign(false);
    }
  };

  const openCaps = (u) => {
    setEditingCaps({ id: u._id, username: u.username, set: new Set(u.capabilities && u.capabilities.length ? u.capabilities : ['article']) });
  };

  const toggleCap = (key) => {
    setEditingCaps(a => {
      const set = new Set(a.set);
      set.has(key) ? set.delete(key) : set.add(key);
      return { ...a, set };
    });
  };

  const handleSaveCaps = async () => {
    setSavingCaps(true);
    try {
      await userApi.update(editingCaps.id, { capabilities: Array.from(editingCaps.set) });
      setEditingCaps(null);
      load();
    } finally {
      setSavingCaps(false);
    }
  };

  const handleToggleActive = async (u) => {
    await userApi.update(u._id, { active: !u.active });
    load();
  };

  const handleResetPassword = async (u) => {
    const pw = window.prompt(`为「${u.username}」设置新密码：`);
    if (!pw || !pw.trim()) return;
    await userApi.update(u._id, { password: pw.trim() });
    alert('密码已重置');
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`确认删除子账号「${u.username}」？删除后该账号无法登录。`)) return;
    await userApi.delete(u._id);
    load();
  };

  const userCaps = (u) => (u.capabilities && u.capabilities.length ? u.capabilities : ['article']);

  return (
    <div style={styles.page}>
      <AdminHeader
        title="账号管理"
        back="/admin"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <GhostBtn small onClick={() => auth.logout()}>退出登录</GhostBtn>
            <PrimaryBtn onClick={() => setCreating(true)}>新建子账号</PrimaryBtn>
          </div>
        }
      />

      <div style={styles.body}>
        <p style={styles.hint}>子账号按功能权限分工：文章运营撰写文章、探店运营管理博主与探店活动。只能操作被分配的商家。</p>

        {loading ? (
          <div style={styles.skeletons}>{[1, 2, 3].map(i => <div key={i} style={styles.skeleton} />)}</div>
        ) : users.length === 0 ? (
          <EmptyState text="还没有子账号，点击右上角新建" />
        ) : (
          <div style={styles.list}>
            {users.map((u) => (
              <div key={u._id} style={styles.card}>
                <div style={styles.cardTop}>
                  <div style={styles.userInfo}>
                    <span style={styles.username}>{u.username}</span>
                    <span style={u.active ? styles.badgeOn : styles.badgeOff}>
                      {u.active ? '启用中' : '已停用'}
                    </span>
                    {u.role === 'superadmin' && <span style={styles.badgeAdmin}>超管</span>}
                  </div>
                </div>

                {u.role !== 'superadmin' && (
                  <div style={styles.capRow}>
                    {userCaps(u).map(c => <span key={c} style={styles.capTag}>{capLabel(c)}</span>)}
                  </div>
                )}

                <div style={styles.merchantsRow}>
                  {u.role === 'superadmin' ? (
                    <span style={styles.allTag}>可见全部商家</span>
                  ) : (u.assignedMerchants || []).length === 0 ? (
                    <span style={styles.noneTag}>未分配商家</span>
                  ) : (
                    (u.assignedMerchants || []).map(id => (
                      <span key={id} style={styles.mTag}>{merchantName(id)}</span>
                    ))
                  )}
                </div>

                {u.role !== 'superadmin' && (
                  <div style={styles.actions}>
                    <button style={styles.actBtn} onClick={() => openCaps(u)}>功能权限</button>
                    <button style={styles.actBtn} onClick={() => openAssign(u)}>分配商家</button>
                    <button style={styles.actBtn} onClick={() => handleResetPassword(u)}>重置密码</button>
                    <button style={styles.actBtn} onClick={() => handleToggleActive(u)}>
                      {u.active ? '停用' : '启用'}
                    </button>
                    <button style={styles.actBtnDanger} onClick={() => handleDelete(u)}>删除</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 新建子账号 */}
      {creating && (
        <div style={styles.overlay} onClick={() => setCreating(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p style={styles.modalTitle}>新建子账号</p>
            <input
              style={styles.input}
              placeholder="登录账号"
              value={form.username}
              onChange={(e) => setForm(f => ({ ...f, username: e.target.value }))}
              autoCapitalize="none"
              autoFocus
            />
            <input
              style={styles.input}
              placeholder="登录密码"
              value={form.password}
              onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
            />
            <p style={styles.modalLabel}>功能权限（可多选）</p>
            <div style={styles.capChoose}>
              {CAP_OPTIONS.map(c => (
                <label key={c.key} style={styles.capChooseRow}>
                  <input type="checkbox" checked={form.capabilities.includes(c.key)} onChange={() => toggleFormCap(c.key)} />
                  <span style={styles.checkName}>{c.label}</span>
                </label>
              ))}
            </div>
            <p style={styles.modalLabel}>分配商家（可多选，之后也能改）</p>
            <div style={styles.checkList}>
              {merchants.length === 0 ? (
                <span style={styles.noneTag}>暂无商家，请先在商家列表添加</span>
              ) : merchants.map((m) => (
                <label key={m._id} style={styles.checkRow}>
                  <input
                    type="checkbox"
                    checked={form.assignedMerchants.includes(m._id)}
                    onChange={() => toggleFormMerchant(m._id)}
                  />
                  <span style={styles.checkName}>{m.name}</span>
                </label>
              ))}
            </div>
            <div style={styles.modalBtns}>
              <GhostBtn onClick={() => setCreating(false)}>取消</GhostBtn>
              <PrimaryBtn onClick={handleCreate} disabled={saving || !form.username.trim() || !form.password.trim()}>
                {saving ? '创建中...' : '创建'}
              </PrimaryBtn>
            </div>
          </div>
        </div>
      )}

      {/* 功能权限 */}
      {editingCaps && (
        <div style={styles.overlay} onClick={() => setEditingCaps(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p style={styles.modalTitle}>为「{editingCaps.username}」设置功能权限</p>
            <div style={styles.capChoose}>
              {CAP_OPTIONS.map(c => (
                <label key={c.key} style={styles.capChooseRow}>
                  <input type="checkbox" checked={editingCaps.set.has(c.key)} onChange={() => toggleCap(c.key)} />
                  <span style={styles.checkName}>{c.label}</span>
                </label>
              ))}
            </div>
            <div style={styles.modalBtns}>
              <GhostBtn onClick={() => setEditingCaps(null)}>取消</GhostBtn>
              <PrimaryBtn onClick={handleSaveCaps} disabled={savingCaps}>
                {savingCaps ? '保存中...' : '保存'}
              </PrimaryBtn>
            </div>
          </div>
        </div>
      )}

      {/* 分配商家 */}
      {assigning && (
        <div style={styles.overlay} onClick={() => setAssigning(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p style={styles.modalTitle}>为「{assigning.username}」分配商家</p>
            <div style={styles.checkList}>
              {merchants.length === 0 ? (
                <span style={styles.noneTag}>暂无商家</span>
              ) : merchants.map((m) => (
                <label key={m._id} style={styles.checkRow}>
                  <input
                    type="checkbox"
                    checked={assigning.set.has(m._id)}
                    onChange={() => toggleAssign(m._id)}
                  />
                  <span style={styles.checkName}>{m.name}</span>
                </label>
              ))}
            </div>
            <div style={styles.modalBtns}>
              <GhostBtn onClick={() => setAssigning(null)}>取消</GhostBtn>
              <PrimaryBtn onClick={handleSaveAssign} disabled={savingAssign}>
                {savingAssign ? '保存中...' : '保存'}
              </PrimaryBtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f7f7f8', display: 'flex', flexDirection: 'column' },
  body: { flex: 1, padding: '12px 12px 24px' },
  hint: { fontSize: 12, color: '#9ca3af', margin: '0 4px 12px', lineHeight: 1.5 },
  skeletons: { display: 'flex', flexDirection: 'column', gap: 10 },
  skeleton: { height: 96, borderRadius: 12, background: '#efefef' },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: { background: '#fff', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 6px rgba(0,0,0,0.05)' },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  userInfo: { display: 'flex', alignItems: 'center', gap: 8 },
  username: { fontSize: 16, fontWeight: 700, color: '#111' },
  badgeOn: { fontSize: 11, color: '#16a34a', background: '#f0fdf4', padding: '2px 7px', borderRadius: 5, fontWeight: 600 },
  badgeOff: { fontSize: 11, color: '#9ca3af', background: '#f3f4f6', padding: '2px 7px', borderRadius: 5, fontWeight: 600 },
  badgeAdmin: { fontSize: 11, color: '#FA1C51', background: '#fff0f3', padding: '2px 7px', borderRadius: 5, fontWeight: 600 },
  capRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 },
  capTag: { fontSize: 11, color: '#FA1C51', background: '#fff0f3', padding: '3px 9px', borderRadius: 6, fontWeight: 600 },
  merchantsRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 },
  mTag: { fontSize: 12, color: '#374151', background: '#f3f4f6', padding: '3px 9px', borderRadius: 6 },
  allTag: { fontSize: 12, color: '#FA1C51', background: '#fff0f3', padding: '3px 9px', borderRadius: 6, fontWeight: 600 },
  noneTag: { fontSize: 12, color: '#9ca3af' },
  actions: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  actBtn: {
    padding: '6px 12px', background: '#fff', color: '#374151',
    border: '1px solid #e5e7eb', borderRadius: 7, cursor: 'pointer', fontSize: 13,
  },
  actBtnDanger: {
    padding: '6px 12px', background: '#fff', color: '#ef4444',
    border: '1px solid #fecaca', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modal: { background: '#fff', borderRadius: 14, padding: 20, width: '100%', maxWidth: 360, maxHeight: '82vh', display: 'flex', flexDirection: 'column' },
  modalTitle: { fontSize: 15, fontWeight: 700, color: '#111', margin: '0 0 12px' },
  modalLabel: { fontSize: 12, color: '#6b7280', margin: '4px 0 6px', fontWeight: 500 },
  input: {
    width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb',
    borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 10,
  },
  capChoose: { display: 'flex', gap: 10, border: '1px solid #f0f0f0', borderRadius: 8, padding: '8px 12px', marginBottom: 14 },
  capChooseRow: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' },
  checkList: { overflowY: 'auto', flex: 1, border: '1px solid #f0f0f0', borderRadius: 8, padding: '4px 0', marginBottom: 14 },
  checkRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer' },
  checkName: { fontSize: 14, color: '#111' },
  modalBtns: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
};
